/**
 * features/comments/HodComment.tsx
 * FEATURE #2 — Head of Year writes the comment "on behalf of the
 * principal", now via the Al-Nur-style dual-pane review workspace
 * (sees marks + subject teacher comments before writing, with a
 * bottom student-navigator). HOY is scoped to their year groups.
 */
import { useMemo } from 'react';
import { useData } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { isTermEditable, canEnterHodCommentFor } from '@/lib/permissions';
import { TermKey } from '@/lib/school.config';
import type { TermState } from '@/types';
import ReviewWorkspace from './ReviewWorkspace';

const TEMPLATES = [
  {
    label: 'Strong progress',
    phrases: [
      'has had an excellent term and is a credit to the year group.',
      'continues to perform to a high standard across subjects.',
      'should be commended for consistent effort and maturity.',
    ],
  },
  {
    label: 'Encouragement',
    phrases: [
      'is encouraged to maintain this trajectory next term.',
      'should seek additional support in weaker subjects.',
      'has the potential to achieve more with focused effort.',
    ],
  },
  {
    label: 'Concern',
    phrases: [
      'must apply more consistent effort across all subjects.',
      'is urged to use the holiday for focused revision.',
      'needs to settle to a serious study routine to improve.',
    ],
  },
];

export default function HodComment({
  year,
  term,
}: {
  year: string;
  term: TermKey;
}) {
  const { classes, activeStudents: students, termStatuses } = useData();
  const { userData, userRole } = useAuth();

  const termState: TermState =
    termStatuses.find((t) => t.year === year && t.term === term)
      ?.status ?? 'open';
  const editable = isTermEditable(termState, userRole);

  const myClasses = useMemo(() => {
    if (userRole === 'admin' || userRole === 'headteacher')
      return classes;
    if (userRole !== 'hoy') return [];
    const forms = userData?.hoyForms;
    if (!forms || forms.length === 0) return classes;
    return classes.filter((c) => forms.includes(String(c.form)));
  }, [classes, userData, userRole]);

  function canEditStudent(studentId: string) {
    const s = students.find((x) => x.id === studentId);
    return (
      !!s && canEnterHodCommentFor(userData, s, classes) && editable
    );
  }

  return (
    <ReviewWorkspace
      year={year}
      term={term}
      field="hodComment"
      title="Head of Year Review"
      subtitle="Comment on behalf of the Principal — review marks & subject comments first"
      classes={myClasses}
      editable={editable}
      termStateLabel={termState}
      canEditStudent={canEditStudent}
      templates={TEMPLATES}
    />
  );
}
